<template>
  <div class="search-page">
    <template v-if="showEmptyState">
      <div class="search-page__empty">
        <div class="container">
          <p class="search-page__empty-message">
            Sorry, we can't find anything matching your search. Try a different
            keyword or explore our trending categories.
          </p>
          <div class="search-page__cards">
            <SearchCard
              v-for="(link, index) in searchNavigationCards"
              :key="index"
              :link="link"
            />
          </div>
        </div>
        <div class="search-page__bestsellers">
          <HomepageRecommendationsCarousel
            title="BESTSELLERS"
            subtitle="Discover this season's most-wanted."
            link="/collections/best-sellers"
            collection-handle="best-sellers"
            collection-only
            slide-width="310px"
            slide-width-tablet="310px"
            slide-width-desktop="300px"
          />
        </div>
      </div>
    </template>
    <template v-else>
      <div v-show="!loading" class="search-page__heading">
        <div class="container">
          <span class="search-page__term">"{{ searchTerm }}"</span>
        </div>
      </div>
      <div v-if="useNostoSearch && hasFacets" class="containerV2">
        <div class="search-page__toolbar">
          <p class="search-page__count">{{ productCount }} Products</p>
          <IconButton
            icon="filter"
            icon-position="right"
            form="transparent"
            class="search-page__filter-btn"
            @click="setIsFiltersOpen(true)"
          >
            Filters
          </IconButton>
        </div>
      </div>
      <div class="search-page__results">
        <ProductGridV2
          :products="productsWithSkeleton"
          @productClick="handleProductClick"
        />
        <Pagination v-if="showPagination" @loadMore="handleLoadMore" />
      </div>
      <NostoSearchFilters
        v-if="useNostoSearch"
        :facets="nostoFacets"
        @filterChange="handleNostoFilterChange"
      />
    </template>
  </div>
</template>

<script>
import { mapGetters, mapState, mapActions } from "vuex";
import isEmpty from "lodash-es/isEmpty";
import Pagination from "@/components/collection/Pagination.vue";
import ProductGridV2 from "@/components/collection/ProductGridV2.vue";
import HomepageRecommendationsCarousel from "@/components/homepage/HomepageRecommendationsCarousel.vue";
import SearchCard from "@/components/navigation/search/SearchCard.vue";
import IconButton from "@/components/IconButton.vue";
import NostoSearchFilters from "@/components/search/NostoSearchFilters.vue";
import searchQuery from "@/graphql/queries/SearchV2.gql";
import searchProductsByIdsQuery from "@/graphql/queries/SearchProductsByIds.gql";
import { PER_PAGE } from "@/helpers/pagination";
import { getParams } from "@/helpers/url.js";
import { getFeatureToggles } from "@/helpers/featureToggles";
import { getSearchNavigationLinks } from "@/helpers/searchNavigationLinks";
import {
  nostoProductSearch,
  recordNostoSearchClick,
  consumeNostoKeywordFlag,
  toNostoProductId,
} from "@/helpers/nostoSearch";

export default {
  name: "SearchPage",
  components: {
    ProductGridV2,
    Pagination,
    HomepageRecommendationsCarousel,
    SearchCard,
    IconButton,
    NostoSearchFilters,
  },
  data() {
    return {
      searchTerm: "",
      search: null,
      lastPaginationCursor: null,
      isFetchingMore: false,
      nostoFrom: 0,
      nostoFacets: [],
      nostoSelectedFilters: [],
      nostoSearchStarted: false,
      nostoIsKeywordSearch: false,
      isRedirecting: false,
    };
  },
  computed: {
    ...mapState("collection", ["collection", "loading", "filters", "sort"]),
    ...mapGetters("collection", ["products", "productCount"]),
    ...mapGetters("app", ["currencyCode"]),
    hasFacets() {
      return this.nostoFacets.length > 0;
    },
    useNostoSearch() {
      const { nostoSearchEnabled = false } = getFeatureToggles();
      return nostoSearchEnabled;
    },
    skeletonProducts() {
      return Array.from({ length: PER_PAGE }, (_, index) => ({
        skeleton: true,
        id: `skeleton-${index}`,
      }));
    },
    productsWithSkeleton() {
      const products = [...this.products];
      if (this.loading) {
        return [...products, ...this.skeletonProducts];
      }
      return products;
    },
    showPagination() {
      return this.collection?.pageInfo?.hasNextPage;
    },
    showEmptyState() {
      if (!this.searchTerm || this.loading) {
        return false;
      }

      const totalCount = this.collection?.totalCount;

      if (totalCount !== undefined && totalCount !== null) {
        return totalCount === 0;
      }

      return this.products.length === 0;
    },
    searchNavigationCards() {
      return getSearchNavigationLinks(this.currencyCode).slice(0, 4);
    },
  },
  watch: {
    currencyCode() {
      this.maybeStartNostoSearch();
    },
  },
  mounted() {
    const params = getParams();
    const queryTerm = params.q;
    if (queryTerm) {
      this.searchTerm = queryTerm.trim();
    }

    // The typeahead flags keyword-suggestion clicks before navigating here, so
    // the first tracked search can be attributed as a keyword search.
    this.nostoIsKeywordSearch = consumeNostoKeywordFlag(this.searchTerm);

    // currencyCode is resolved from the shop query, which is fetched
    // asynchronously on app load. The Nosto account id is derived from it, so
    // hold off until it's available (otherwise the request bails with no
    // account id and silently falls back to Shopify search with no facets).
    this.maybeStartNostoSearch();
  },
  methods: {
    ...mapActions("collection", [
      "setCollection",
      "setLoading",
      "setFilters",
      "setPriceRange",
      "setIsFiltersOpen",
    ]),
    maybeStartNostoSearch() {
      if (!this.useNostoSearch || this.nostoSearchStarted) {
        return;
      }

      if (!this.currencyCode) {
        return;
      }

      this.nostoSearchStarted = true;
      this.fetchNostoResults();
    },
    buildEdges(products, startIndex) {
      return products.map((node, index) => ({
        cursor: String(startIndex + index),
        node,
      }));
    },
    async hydrateProductIds(productIds) {
      if (!productIds.length) {
        return [];
      }

      const ids = productIds.map((id) => `gid://shopify/Product/${id}`);

      const { data } = await this.$apollo.query({
        query: searchProductsByIdsQuery,
        variables: { ids },
      });

      // nodes(ids:) preserves the requested (Nosto-ranked) order; null entries
      // are dropped (e.g. unpublished products or not available in this region).
      return (data?.nodes || []).filter(Boolean);
    },
    async fetchNostoResults() {
      if (!this.searchTerm) {
        return;
      }

      this.setLoading(true);
      this.nostoFrom = 0;

      // Only the very first search after a keyword click counts as a keyword
      // search; follow-up filter changes are regular serp searches.
      const isKeyword = this.nostoIsKeywordSearch;
      this.nostoIsKeywordSearch = false;

      try {
        const { productIds, total, facets, redirect } =
          await nostoProductSearch({
            currencyCode: this.currencyCode,
            query: this.searchTerm,
            size: PER_PAGE,
            from: 0,
            filters: this.nostoSelectedFilters,
            track: "serp",
            isKeyword,
          });

        // A matching Nosto redirect rule (e.g. "returns", "delivery") forwards
        // the shopper straight to the configured page. Keep loading true so the
        // empty state never flashes while the browser navigates away.
        if (redirect) {
          this.isRedirecting = true;
          window.location.href = redirect;
          return;
        }

        if (facets) {
          this.nostoFacets = facets;
        }

        const products = await this.hydrateProductIds(productIds);

        if (!total || products.length === 0) {
          await this.fetchShopifySearchResults();
          return;
        }

        const edges = this.buildEdges(products, 0);
        const hasNextPage = PER_PAGE < total;
        const pageInfo = { hasNextPage };

        const collection = {
          totalCount: total,
          pageInfo,
          edges,
          products: {
            totalCount: total,
            pageInfo,
            edges,
          },
        };

        this.setCollection(collection);
        this.nostoFrom = PER_PAGE;
      } catch (error) {
        console.log(error);
        await this.fetchShopifySearchResults();
      } finally {
        if (!this.isRedirecting) {
          this.setLoading(false);
        }
      }
    },
    applySearchResults(data) {
      const collection = {
        ...data?.search,
        products: {
          ...data?.search,
        },
      };

      this.setCollection(collection);

      if (isEmpty(this.filters)) {
        const filters = data?.search?.productFilters;
        if (filters) {
          const priceFilter = filters.find(
            (filter) => filter.id === "filter.v.price"
          );

          if (priceFilter) {
            const { price } = JSON.parse(priceFilter.values[0].input);
            this.setPriceRange(price);
          }
        }
      }
    },
    async fetchShopifySearchResults() {
      if (!this.searchTerm) {
        return;
      }

      this.setLoading(true);

      try {
        const { data } = await this.$apollo.query({
          query: searchQuery,
          fetchPolicy: "network-only",
          variables: {
            searchQuery: this.searchTerm,
            first: PER_PAGE,
            sort: "RELEVANCE",
          },
        });

        this.applySearchResults(data);
      } catch (error) {
        console.log(error);
      } finally {
        this.setLoading(false);
      }
    },
    async handleNostoLoadMore() {
      if (this.loading) {
        return;
      }

      if (!this.collection?.pageInfo?.hasNextPage) {
        return;
      }

      this.setLoading(true);

      const from = this.nostoFrom;

      try {
        const { productIds, total } = await nostoProductSearch({
          currencyCode: this.currencyCode,
          query: this.searchTerm,
          size: PER_PAGE,
          from,
          filters: this.nostoSelectedFilters,
          track: "serp",
        });

        const products = await this.hydrateProductIds(productIds);
        const newEdges = this.buildEdges(products, from);
        const mergedEdges = [...(this.collection?.edges || []), ...newEdges];
        const hasNextPage = from + PER_PAGE < total;
        const pageInfo = { hasNextPage };

        const mergedCollection = {
          ...this.collection,
          totalCount: total,
          pageInfo,
          edges: mergedEdges,
          products: {
            ...this.collection.products,
            totalCount: total,
            pageInfo,
            edges: mergedEdges,
          },
        };

        this.setCollection(mergedCollection);
        this.nostoFrom = from + PER_PAGE;
      } catch (error) {
        console.log(error);
      } finally {
        this.setLoading(false);
      }
    },
    handleNostoFilterChange(filters) {
      this.nostoSelectedFilters = filters;
      this.nostoFrom = 0;
      this.fetchNostoResults();
    },
    handleProductClick(product) {
      if (!this.useNostoSearch) {
        return;
      }

      recordNostoSearchClick("serp", {
        productId: toNostoProductId(product?.id),
        url: product?.onlineStoreUrl,
      });
    },
    async handleLoadMore() {
      if (this.useNostoSearch) {
        return this.handleNostoLoadMore();
      }

      if (!this.collection?.pageInfo?.hasNextPage) {
        return;
      }

      if (this.loading || this.isFetchingMore) {
        return;
      }

      this.isFetchingMore = true;
      this.setLoading(true);

      let paginationCursor = null;

      try {
        const edges = this.collection?.edges;
        const lastEdge = edges?.[edges.length - 1];
        paginationCursor = lastEdge?.cursor;

        if (!paginationCursor) {
          return;
        }

        if (this.lastPaginationCursor === paginationCursor) {
          console.warn("Duplicate pagination cursor");
        }

        const resp = await this.$apollo.queries.search.fetchMore({
          variables: {
            after: paginationCursor,
          },
        });

        // Handle the merge manually since Apollo's updateQuery isn't working as expected
        if (resp.data && resp.data.search) {
          const currentCollection = { ...this.collection };
          const newSearchData = resp.data.search;

          const mergedCollection = {
            ...currentCollection,
            ...newSearchData,
            edges: [
              ...(currentCollection.edges || []),
              ...(newSearchData.edges || []),
            ],
            products: {
              ...currentCollection.products,
              ...newSearchData,
              edges: [
                ...(currentCollection.edges || []),
                ...(newSearchData.edges || []),
              ],
            },
          };

          this.setCollection(mergedCollection);
        }
      } catch (error) {
        console.log(error);
      } finally {
        this.setLoading(false);
        this.isFetchingMore = false;
        if (paginationCursor) {
          this.lastPaginationCursor = paginationCursor;
        }
      }
    },
  },
  apollo: {
    search: {
      query: searchQuery,
      skip() {
        return this.useNostoSearch;
      },
      variables() {
        return {
          searchQuery: this.searchTerm,
          first: PER_PAGE,
          sort: "RELEVANCE",
        };
      },
      result(resp) {
        // Skip processing if we're in the middle of a fetchMore operation
        if (this.isFetchingMore) {
          return;
        }

        this.applySearchResults(resp.data);
        this.setLoading(false);
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.search-page__heading {
  text-align: center;
  padding: 61px 0;
}

.search-page__term {
  text-transform: uppercase;
  @include editorial-headline(32px);
}

.search-page__empty {
  padding: 61px 0 0;
}

.search-page__empty-message {
  max-width: 340px;
  margin: 0 auto 60px;
  text-align: center;
  @include editorial-body(14px);
  line-height: 150%;

  @include respond-above(md) {
    margin-bottom: 48px;
  }
}

.search-page__bestsellers {
  margin-top: 60px;
  background-color: #fafafa;
}

.search-page__cards {
  display: flex;
  gap: 20px;
  overflow-x: auto;
  padding-bottom: 12px;

  @include respond-above(md) {
    justify-content: center;
  }

  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.05);
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.35);
  }

  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.35) rgba(0, 0, 0, 0.05);

  & > * {
    flex: 0 0 260px;
    width: 260px;
  }
}

.search-page__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
}

.search-page__count {
  @include utility-label(12px);
}

.search-page__filter-btn {
  :deep(.text) {
    @include utility-label(12px);
  }
}
</style>
